import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { TutorialVideo } from '@/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

interface TutorialPlayerModalProps {
  videos: TutorialVideo[];
  startIndex?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Wird beim Anzeigen jedes Videos gefeuert — Aufrufer markiert als gelesen + trackt. */
  onViewed: (video: TutorialVideo) => void;
}

export const TutorialPlayerModal: React.FC<TutorialPlayerModalProps> = ({
  videos,
  startIndex = 0,
  open,
  onOpenChange,
  onViewed,
}) => {
  const [index, setIndex] = useState(startIndex);
  // Pro Öffnen-Session: bereits gemeldete Video-IDs (genau ein onViewed je gezeigtem Video)
  const firedIdsRef = useRef<Set<string>>(new Set());
  // Beim Öffnen laufen Reset- und onViewed-Effect im selben Commit — `index` (und damit
  // `video`) ist dann noch der Stand vom letzten Öffnen. Das Flag sorgt dafür, dass der
  // erste onViewed-Lauf für das tatsächlich gezeigte (geclampte) Startvideo feuert.
  const pendingResetRef = useRef(false);

  const clampedStartIndex = Math.min(startIndex, Math.max(videos.length - 1, 0));

  // Bei jedem Öffnen auf das Startvideo zurücksetzen; die Dedupe-Session
  // startet nur bei der Öffnen-Flanke neu (nicht bei Clamp-Änderungen mid-open).
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }
    if (!wasOpenRef.current) {
      wasOpenRef.current = true;
      firedIdsRef.current = new Set();
      pendingResetRef.current = true;
    }
    setIndex(clampedStartIndex);
  }, [open, clampedStartIndex]);

  const video = videos[index];

  useEffect(() => {
    if (!open) return;
    const shown = pendingResetRef.current ? videos[clampedStartIndex] : video;
    pendingResetRef.current = false;
    if (!shown || firedIdsRef.current.has(shown.id)) return;
    firedIdsRef.current.add(shown.id);
    onViewed(shown);
  }, [open, video, onViewed, videos, clampedStartIndex]);

  if (!video) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{video.title}</DialogTitle>
          {video.description && <DialogDescription>{video.description}</DialogDescription>}
        </DialogHeader>

        <div className="aspect-video w-full overflow-hidden rounded-lg bg-black">
          <iframe
            key={video.id}
            className="h-full w-full"
            src={`https://www.youtube-nocookie.com/embed/${video.youtubeVideoId}?autoplay=1&rel=0`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        <div className="flex items-center justify-between gap-2">
          <a
            href={`https://www.youtube.com/watch?v=${video.youtubeVideoId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <ExternalLink className="h-3 w-3" /> Auf YouTube ansehen
          </a>
          {videos.length > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={index === 0}
                onClick={() => setIndex(i => i - 1)}
              >
                <ChevronLeft className="h-4 w-4" /> Zurück
              </Button>
              <span className="text-xs text-muted-foreground">
                {index + 1} / {videos.length}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={index === videos.length - 1}
                onClick={() => setIndex(i => i + 1)}
              >
                Weiter <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
